import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

type PlatformKey = "signal" | "imos";

interface RawDocument {
	id?: string | number;
	externalId?: string;
	reportId?: string | number;
	vesselName?: string;
	imoNumber?: string;
	submittedAt?: string;
	queuedAt?: string;
	selectedDate?: string;
	priority?: "critical" | "high" | "normal" | "low";
	status?: string;
	etaMinutes?: number;
	estimatedMinutes?: number;
	position?: number;
	lastError?: string;
	platform?: PlatformKey | string;
}

interface RawQueue {
	documents?: RawDocument[];
	queue?: RawDocument[];
	pendingCount?: number;
	totalEtaMinutes?: number;
	avgEtaMinutes?: number;
	nextEtaMinutes?: number;
	throughputPerHour?: number;
	batchSize?: number;
	nextCronTime?: string;
}

interface ApiResponse {
	updatedAt?: string;
	refreshIntervalSeconds?: number;
	queues?: Partial<Record<PlatformKey, RawQueue>>;
	signal?: RawQueue;
	imos?: RawQueue;
}

interface CnrDocument {
	id: string;
	externalId?: string;
	vesselName: string;
	platform: PlatformKey;
	submittedAt?: string;
	selectedDate?: string;
	priority: "critical" | "high" | "normal" | "low";
	status: string;
	etaMinutes?: number;
	position: number;
	lastError?: string;
}

interface NormalizedQueue {
	documents: CnrDocument[];
	pendingCount: number;
	totalEtaMinutes: number;
	avgEtaMinutes: number;
	nextEtaMinutes: number;
	throughputPerHour: number;
	batchSize: number;
	nextCronTime?: string;
}

const PLATFORM_META: Record<
	PlatformKey,
	{
		label: string;
		description: string;
		accent: string;
		bgAccent: string;
		chip: string;
		border: string;
		borderTop: string;
		progress: string;
		cardShadow: string;
		gradient: string;
		icon: string;
	}
> = {
	signal: {
		label: "Signal",
		description: "Signal API ingestion pipeline",
		accent: "text-blue-600",
		bgAccent: "bg-blue-600",
		chip: "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20",
		border: "border-blue-100",
		borderTop: "border-t-blue-500",
		progress: "bg-blue-500",
		cardShadow: "shadow-slate-200/50",
		gradient: "from-white to-slate-50",
		icon: "bp3-icon-graph",
	},
	imos: {
		label: "IMOS",
		description: "IMOS integration pipeline",
		accent: "text-emerald-600",
		bgAccent: "bg-emerald-600",
		chip: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20",
		border: "border-emerald-100",
		borderTop: "border-t-emerald-500",
		progress: "bg-emerald-500",
		cardShadow: "shadow-slate-200/50",
		gradient: "from-white to-slate-50",
		icon: "bp3-icon-path-search",
	},
};

const DEFAULT_QUEUE: NormalizedQueue = {
	documents: [],
	pendingCount: 0,
	totalEtaMinutes: 0,
	avgEtaMinutes: 0,
	nextEtaMinutes: 0,
	throughputPerHour: 0,
	batchSize: 1,
	nextCronTime: undefined,
};

const PRIORITY_COLORS: Record<
	CnrDocument["priority"],
	string
> = {
	critical: "bg-red-50 text-red-700",
	high: "bg-amber-50 text-amber-700",
	normal: "bg-slate-100 text-slate-700",
	low: "bg-slate-50 text-slate-500",
};

const STATUS_COLORS: Record<string, string> = {
	processing: "bg-blue-50 text-blue-700",
	queued: "bg-slate-100 text-slate-700",
	paused: "bg-amber-50 text-amber-700",
	deferred: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20",
	error: "bg-red-50 text-red-700",
	completed: "bg-emerald-50 text-emerald-700",
	stuck: "bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20",
};

const formatDuration = (minutes?: number) => {
	if (minutes === undefined || Number.isNaN(minutes)) {
		return "—";
	}
	if (minutes < 1) {
		return "<1m";
	}
	const hrs = Math.floor(minutes / 60);
	const mins = Math.round(minutes % 60);
	if (hrs === 0) {
		return `${mins}m`;
	}
	return `${hrs}h ${mins}m`;
};

const formatRelativeTime = (iso?: string) => {
	if (!iso) return "—";
	const diffMs = Date.now() - new Date(iso).getTime();
	const diffMinutes = Math.round(diffMs / (1000 * 60));
	if (diffMinutes < 1) return "just now";
	if (diffMinutes < 60) return `${diffMinutes}m ago`;
	const diffHours = Math.round(diffMinutes / 60);
	if (diffHours < 24) return `${diffHours}h ago`;
	const diffDays = Math.round(diffHours / 24);
	return `${diffDays}d ago`;
};

const formatDateTime = (iso?: string) => {
	if (!iso) return "—";
	return new Date(iso).toLocaleString();
};

const formatCronTime = (iso?: string) => {
	if (!iso) return "—";
	return new Date(iso).toLocaleTimeString(undefined, {
		hour: "2-digit",
		minute: "2-digit",
	});
};

const normalizeQueue = (platform: PlatformKey, queue?: RawQueue): NormalizedQueue => {
	if (!queue) {
		return DEFAULT_QUEUE;
	}

	const rawDocuments = queue.documents ?? queue.queue ?? [];

	const hasPrecomputedMetrics =
		typeof queue.totalEtaMinutes === "number" ||
		typeof queue.avgEtaMinutes === "number" ||
		typeof queue.nextEtaMinutes === "number" ||
		typeof queue.throughputPerHour === "number";

	if (hasPrecomputedMetrics) {
		const documents: CnrDocument[] = rawDocuments.map((doc, index) => ({
			id: String(doc.id ?? doc.externalId ?? doc.reportId ?? `${platform}-${index}`),
			externalId: doc.externalId ?? String(doc.id ?? doc.reportId ?? index + 1),
			vesselName: doc.vesselName ?? doc.imoNumber ?? "Unknown vessel",
			platform,
			selectedDate: doc.selectedDate,
			submittedAt: doc.submittedAt ?? doc.queuedAt,
			priority: doc.priority ?? (index === 0 ? "high" : "normal"),
			status: (doc.status ?? (index === 0 ? "processing" : "queued")).toLowerCase(),
			etaMinutes: doc.etaMinutes ?? doc.estimatedMinutes,
			position: doc.position ?? index + 1,
			lastError: doc.lastError,
		}));

		const throughputValue =
			typeof queue.throughputPerHour === "number"
				? queue.throughputPerHour
				: DEFAULT_QUEUE.throughputPerHour;

		return {
			documents,
			pendingCount: queue.pendingCount ?? documents.length,
			totalEtaMinutes: Math.round(queue.totalEtaMinutes ?? 0),
			avgEtaMinutes: Math.round(queue.avgEtaMinutes ?? DEFAULT_QUEUE.avgEtaMinutes),
			nextEtaMinutes: Math.round(
				queue.nextEtaMinutes ?? documents[0]?.etaMinutes ?? 0
			),
			throughputPerHour: Number(throughputValue.toFixed(2)),
			batchSize: queue.batchSize ?? DEFAULT_QUEUE.batchSize,
			nextCronTime: queue.nextCronTime ?? DEFAULT_QUEUE.nextCronTime,
		};
	}

	const documents: CnrDocument[] = rawDocuments.map((doc, index) => ({
		id: String(doc.id ?? doc.externalId ?? doc.reportId ?? `${platform}-${index}`),
		externalId: doc.externalId ?? String(doc.id ?? doc.reportId ?? index + 1),
		vesselName: doc.vesselName ?? doc.imoNumber ?? "Unknown vessel",
		platform,
		selectedDate: doc.selectedDate,
		submittedAt: doc.submittedAt ?? doc.queuedAt,
		priority: doc.priority ?? (index === 0 ? "high" : "normal"),
		status: (doc.status ?? (index === 0 ? "processing" : "queued")).toLowerCase(),
		etaMinutes: doc.etaMinutes ?? doc.estimatedMinutes,
		position: doc.position ?? index + 1,
		lastError: doc.lastError,
	}));

	const pendingCount = queue?.pendingCount ?? documents.length;
	const avgEta =
		queue?.avgEtaMinutes ??
		(documents.length
			? documents.reduce((acc, doc) => acc + (doc.etaMinutes ?? 0), 0) /
			  documents.length
			: 0);

	const totalEta =
		queue?.totalEtaMinutes ??
		documents.reduce(
			(acc, doc) => acc + (doc.etaMinutes ?? avgEta),
			0
		);

	const nextEta =
		queue?.nextEtaMinutes ??
		documents[0]?.etaMinutes ??
		avgEta;

	const throughput =
		queue?.throughputPerHour ??
		(totalEta > 0 ? (pendingCount / totalEta) * 60 : 0);

	return {
		documents,
		pendingCount,
		totalEtaMinutes: Math.round(totalEta),
		avgEtaMinutes: Math.round(avgEta),
		nextEtaMinutes: Math.round(nextEta),
		throughputPerHour: Number(throughput.toFixed(2)),
		batchSize: DEFAULT_QUEUE.batchSize,
		nextCronTime: DEFAULT_QUEUE.nextCronTime,
	};
};

const CNRProcessingStatus: React.FC = () => {
	const [rawData, setRawData] = useState<ApiResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lastUpdated, setLastUpdated] = useState<string | undefined>();
	const [refreshSeconds, setRefreshSeconds] = useState(60);
	const hasLoadedRef = useRef(false);

	const fetchStatus = useCallback(async () => {
		setError(null);
		if (hasLoadedRef.current) {
			setIsRefreshing(true);
		} else {
			setLoading(true);
		}
		try {
			const response = await axios.get<ApiResponse>("/api/cnr-processing-status");
			setRawData(response.data);
			setLastUpdated(response.data.updatedAt ?? new Date().toISOString());
			if (response.data.refreshIntervalSeconds) {
				setRefreshSeconds(response.data.refreshIntervalSeconds);
			}
			hasLoadedRef.current = true;
			setLoading(false);
		} catch (err) {
			const message =
				err instanceof Error
					? err.message
					: "Unable to load queue status right now.";
			setError(message);
			setLoading(false);
		} finally {
			setIsRefreshing(false);
		}
	}, []);

	useEffect(() => {
		fetchStatus();
	}, [fetchStatus]);

	useEffect(() => {
		if (!refreshSeconds) return;
		const timer = setInterval(() => {
			fetchStatus();
		}, refreshSeconds * 1000);
		return () => clearInterval(timer);
	}, [fetchStatus, refreshSeconds]);

	const queues = useMemo(() => {
		return {
			signal: normalizeQueue(
				"signal",
				rawData?.queues?.signal ?? rawData?.signal
			),
			imos: normalizeQueue("imos", rawData?.queues?.imos ?? rawData?.imos),
		};
	}, [rawData]);

	const totalPending = queues.signal.pendingCount + queues.imos.pendingCount;
	const totalEta = queues.signal.totalEtaMinutes + queues.imos.totalEtaMinutes;
	const combinedThroughput = Number(
		(queues.signal.throughputPerHour + queues.imos.throughputPerHour).toFixed(1)
	);

	const overviewStats = [
		{
			label: "Documents in queue",
			value: loading ? "…" : totalPending,
			helper: `${queues.signal.pendingCount} Signal • ${queues.imos.pendingCount} IMOS`,
			icon: "bp3-icon-layers",
		},
		{
			label: "Combined queue ETA",
			value: loading ? "…" : formatDuration(totalEta),
			helper: `Next up ${formatDuration(queues.signal.nextEtaMinutes)} (Signal)`,
			icon: "bp3-icon-time",
		},
		{
			label: "Throughput / Last sync",
			value: loading ? "…" : `${combinedThroughput} docs/hr`,
			helper: `Refreshed ${formatRelativeTime(lastUpdated)} • every ${refreshSeconds}s`,
			icon: "bp3-icon-pulse",
		},
	];

	return (
		<div className="min-h-screen bg-slate-50/50 py-12 px-4 md:px-8 font-sans">
			<div className="mx-auto max-w-7xl space-y-12">
				<header className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex items-center gap-4">
						<button
							onClick={() => window.history.back()}
							className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
							aria-label="Go back"
						>
							<span className="bp3-icon bp3-icon-arrow-left" />
						</button>
						<div>
							<h1 className="text-2xl font-bold text-slate-900 tracking-tight">
								CNR Processing Monitor
							</h1>
							<div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 font-medium mt-1">
								<span className="flex items-center gap-1.5">
									<span className={`h-1.5 w-1.5 rounded-full ${isRefreshing ? "bg-blue-500 animate-pulse" : "bg-emerald-500"}`} />
									{isRefreshing ? "Updating..." : `Updated ${formatDateTime(lastUpdated)}`}
								</span>
								<span className="hidden sm:block h-1 w-1 rounded-full bg-slate-300" />
								<span>Auto-refresh {refreshSeconds}s</span>
							</div>
						</div>
					</div>
					<div className="flex items-center gap-3 pl-14 sm:pl-0">
						<button
							onClick={fetchStatus}
							disabled={loading || isRefreshing}
							className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
						>
							<span className={`bp3-icon bp3-icon-refresh ${isRefreshing ? "animate-spin" : ""}`} />
							Refresh
						</button>
					</div>
				</header>

				<section className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-6">
					{overviewStats.map((stat) => (
						<StatCard
							key={stat.label}
							label={stat.label}
							value={stat.value}
							helper={stat.helper}
							icon={stat.icon}
						/>
					))}
				</section>

				{error && (
					<div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
						<div className="font-semibold">Unable to load queue status</div>
						<p className="mt-1 text-red-600">{error}</p>
					</div>
				)}

				<section className="space-y-6">
					<SectionHeading
						title="Queue health by platform"
						subtitle="Capacity and cadence for each integration"
					/>
					<div className="grid gap-6 md:grid-cols-2 lg:gap-8">
						{(Object.keys(PLATFORM_META) as PlatformKey[]).map((platform) => (
							<PlatformSummaryCard
								key={platform}
								platform={platform}
								queue={queues[platform]}
								loading={loading}
							/>
						))}
					</div>
				</section>

				<section className="space-y-6">
					<SectionHeading
						title="Documents in queue"
						subtitle="Latest Signal and IMOS records pending ingestion"
					/>
					<QueueTabs queues={queues} loading={loading} />
				</section>
			</div>
		</div>
	);
};

const QueueTabs = ({
	queues,
	loading,
}: {
	queues: Record<PlatformKey, NormalizedQueue>;
	loading: boolean;
}) => {
	const tabs = (Object.keys(PLATFORM_META) as PlatformKey[]).map((platform) => ({
		id: platform,
		label: PLATFORM_META[platform].label,
		icon: PLATFORM_META[platform].icon,
	}));
	const [activeTab, setActiveTab] = useState<PlatformKey>(tabs[0].id);

	return (
		<div className="rounded-[28px] border border-slate-200 bg-white shadow-lg shadow-slate-950/5">
			<div className="flex flex-wrap gap-3 border-b border-slate-100 px-6 py-4">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
							activeTab === tab.id
								? "bg-white text-slate-900 shadow-sm shadow-slate-900/20 border border-slate-200"
								: "bg-slate-100 text-slate-600 hover:bg-slate-200"
						}`}
						onClick={() => setActiveTab(tab.id)}
					>
						<span
							className={`bp3-icon ${tab.icon} ${
								activeTab === tab.id ? "text-slate-900" : "text-slate-500"
							}`}
							aria-hidden
						/>
						{tab.label}
					</button>
				))}
			</div>
			<div className="space-y-3 px-2 py-4 sm:px-4">
				<DocumentQueueCard
					platform={activeTab}
					queue={queues[activeTab]}
					loading={loading}
				/>
			</div>
		</div>
	);
};

const SectionHeading = ({
	title,
	subtitle,
}: {
	title: string;
	subtitle: string;
}) => (
	<div className="flex flex-col gap-1">
		<h2 className="text-lg font-semibold text-slate-900">{title}</h2>
		<p className="text-sm text-slate-500">{subtitle}</p>
	</div>
);

const PlatformSummaryCard = ({
	platform,
	queue,
	loading,
}: {
	platform: PlatformKey;
	queue: NormalizedQueue;
	loading: boolean;
}) => {
	const meta = PLATFORM_META[platform];
	const metrics = [
		{
			label: "Total ETA",
			value: loading ? "…" : formatDuration(queue.totalEtaMinutes),
		},
		{
			label: "Next Run",
			value: loading ? "…" : formatCronTime(queue.nextCronTime),
		},
		{
			label: "Avg / Doc",
			value: loading ? "…" : formatDuration(queue.avgEtaMinutes),
		},
	];

	return (
		<div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
			<div className={`h-1.5 w-full ${meta.progress}`} />
			<div className="flex flex-1 flex-col p-6">
				<div className="mb-6 flex items-start justify-between">
					<div className="flex items-center gap-4">
						<div
							className={`flex h-12 w-12 items-center justify-center rounded-xl ${meta.chip}`}
						>
							<span className={`bp3-icon ${meta.icon} text-xl`} aria-hidden />
						</div>
						<div>
							<h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
								{meta.label}
							</h3>
							<div className="mt-0.5 flex items-baseline gap-2">
								<span className="text-3xl font-bold text-slate-900">
									{loading ? "…" : queue.pendingCount}
								</span>
								<span className="text-sm font-medium text-slate-500">
									pending
								</span>
							</div>
						</div>
					</div>
					<div className="text-right">
						<span
							className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ${meta.chip}`}
						>
							Batch {queue.batchSize}
						</span>
						<p className="mt-1 text-[10px] font-medium text-slate-400">
							{queue.throughputPerHour}/hr
						</p>
					</div>
				</div>

				<div className="mb-6 space-y-2">
					<div className="flex justify-between text-xs font-medium text-slate-500">
						<span>Queue Load</span>
						<span>
							{queue.pendingCount > 0
								? Math.min(queue.pendingCount * 5, 100)
								: 0}
							%
						</span>
					</div>
					<div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
						<div
							className={`h-full rounded-full transition-all duration-500 ${meta.progress}`}
							style={{
								width: `${Math.min(Math.max(queue.pendingCount * 5, 0), 100)}%`,
								backgroundColor: platform === "signal" ? "#3b82f6" : "#10b981",
							}}
						/>
					</div>
				</div>

				<div className="mt-auto grid grid-cols-3 gap-4 border-t border-slate-100 pt-5">
					{metrics.map((metric, i) => (
						<div
							key={metric.label}
							className={
								i !== 0 ? "border-l border-slate-100 pl-4" : ""
							}
						>
							<p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
								{metric.label}
							</p>
							<p className="mt-1 text-sm font-bold text-slate-700">
								{metric.value}
							</p>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

const StatCard = ({
	label,
	value,
	helper,
	icon,
}: {
	label: string;
	value: React.ReactNode;
	helper?: string;
	icon?: string;
}) => {
	return (
		<div className="rounded-xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
			<div className="flex items-start justify-between">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
						{label}
					</p>
					<p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
					{helper && <p className="mt-1 text-xs text-slate-500">{helper}</p>}
				</div>
				{icon && (
					<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
						<span className={`bp3-icon ${icon} text-lg`} aria-hidden />
					</div>
				)}
			</div>
		</div>
	);
};

const DocumentQueueCard = ({
	platform,
	queue,
	loading,
}: {
	platform: PlatformKey;
	queue: NormalizedQueue;
	loading: boolean;
}) => {
	const meta = PLATFORM_META[platform];
	return (
		<div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
			<div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-6 py-4">
				<div className="flex items-center gap-3">
					<h3 className="text-sm font-semibold text-slate-900">
						Processing Queue
					</h3>
					<span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-600">
						{queue.pendingCount}
					</span>
				</div>
				<div className="flex items-center gap-4 text-xs text-slate-500">
					<div className="flex items-center gap-2">
						<span>Load</span>
						<div className="h-2 w-20 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
							<div
								className={`h-full rounded-full ${meta.progress}`}
								style={{
									width: `${Math.min(Math.max(queue.pendingCount * 5, 0), 100)}%`,
									backgroundColor: platform === "signal" ? "#3b82f6" : "#10b981",
								}}
							/>
						</div>
					</div>
					<span>Avg {formatDuration(queue.avgEtaMinutes)} / doc</span>
				</div>
			</div>

			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-slate-100 text-sm">
					<thead className="bg-white text-left text-xs font-medium text-slate-400">
						<tr>
							<th className="px-6 py-3 font-medium">Document</th>
							<th className="px-6 py-3 font-medium">Selected Date</th>
							<th className="px-6 py-3 font-medium">Status</th>
							<th className="px-6 py-3 font-medium">Est. Completion</th>
							<th className="px-6 py-3 font-medium">Submitted</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-slate-50 text-slate-600">
						{loading ? (
							<tr>
								<td colSpan={5} className="px-6 py-12 text-center">
									<div className="flex flex-col items-center justify-center gap-2 text-slate-400">
										<span className="bp3-icon bp3-icon-refresh animate-spin text-2xl" />
										<span>Syncing queue...</span>
									</div>
								</td>
							</tr>
						) : queue.documents.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-6 py-12 text-center">
									<div className="flex flex-col items-center justify-center gap-2 text-slate-400">
										<span className="bp3-icon bp3-icon-tick-circle text-2xl text-emerald-400" />
										<span>All caught up! No documents pending.</span>
									</div>
								</td>
							</tr>
						) : (
							queue.documents.map((doc, index) => (
								<tr
									key={`${platform}-${doc.id}`}
									className="group transition hover:bg-slate-50"
								>
									<td className="px-6 py-4">
										<div className="flex items-start gap-3">
											<div className={`mt-1 h-2 w-2 rounded-full ${
												doc.priority === 'critical' ? 'bg-red-500' :
												doc.priority === 'high' ? 'bg-amber-500' : 'bg-slate-300'
											}`} />
											<div>
												<div className="font-medium text-slate-900">
													{doc.vesselName}
												</div>
												<div className="mt-0.5 text-xs text-slate-400 font-mono">
													ID: {doc.externalId ?? doc.id}
												</div>
											</div>
										</div>
									</td>
									<td className="px-6 py-4 font-mono text-xs">
										{doc.selectedDate ? doc.selectedDate.split("T")[0] : "—"}
									</td>
									<td className="px-6 py-4">
										<span
											className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
												STATUS_COLORS[doc.status] ?? STATUS_COLORS.queued
											}`}
										>
											<span className={`h-1.5 w-1.5 rounded-full ${
												doc.status === 'processing' ? 'bg-blue-500 animate-pulse' :
												doc.status === 'error' ? 'bg-red-500' :
												doc.status === 'deferred' ? 'bg-amber-500' :
												doc.status === 'stuck' ? 'bg-orange-500' :
												'bg-slate-400'
											}`} />
											{doc.status}
										</span>
										{doc.lastError && (
											<div className="mt-1 text-xs text-red-500 max-w-[200px] truncate" title={doc.lastError}>
												{doc.lastError}
											</div>
										)}
									</td>
									<td className="px-6 py-4 font-medium text-slate-900">
										{formatDuration(doc.etaMinutes ?? queue.avgEtaMinutes)}
									</td>
									<td className="px-6 py-4 text-xs text-slate-400">
										{formatRelativeTime(doc.submittedAt)}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default CNRProcessingStatus;

